import { useCallback, useEffect, useRef } from 'react';

import { useCurrent } from '@/hooks/useCurrent';

import { cartActions } from './cartState';

/**
 * Hook for managing cart initialization logic at the app level.
 * This hook focuses on user session management and cart cleanup,
 * while the actual cart loading is handled by useCart hook.
 */
export const useCartInitialization = () => {
  const { user } = useCurrent();
  const currentUserId = useRef<string | null>(null);

  const resetCart = useCallback(() => {
    currentUserId.current = null;
    cartActions.resetCart();
  }, []);

  // Handle user session changes
  useEffect(() => {
    const userId = user?._id;

    // User logged in or switched
    if (userId && currentUserId.current !== userId) {
      currentUserId.current = userId;
      // The useCart hook will handle the actual initialization
      // We just track the user change here
    }

    // User logged out
    if (!userId && currentUserId.current) {
      resetCart();
    }
  }, [user?._id, resetCart]);

  return {
    resetCart,
    currentUserId: currentUserId.current,
    isUserChanged: currentUserId.current !== user?._id,
  };
};
